import {useIntl} from 'react-intl';
import {Flex, Grid, Text} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';

import {AnswerHeading, type AnswersProps, TextAnswer, ImageAnswer} from './';
import {useTriviaStore} from '../../state';
import type {Answer} from '../../types';

export const TextAndImageAnswers = ({submitAnswerMutation, ...props}: AnswersProps) => {
  const format = useIntl().formatNumber;

  const t = useTranslate();
  const question = useTriviaStore((state) => state.currentQuestion!);

  const isSingleChoice = question.correct_answers_count === 1;
  const questionAnsweredCorrectly = !!submitAnswerMutation.data?.is_correct;

  const onAnswerChange = (answer: Answer) => {
    if (submitAnswerMutation.isLoading) return;

    if (isSingleChoice) {
      return useTriviaStore.setState(() => ({currentAnswer: [answer]}));
    }

    useTriviaStore.setState((state) => {
      const isAnswerSelected = !!state.currentAnswer.find(({id}) => id === answer.id);
      if (isAnswerSelected) {
        return {
          currentAnswer: state.currentAnswer.filter(({id}) => id !== answer.id),
        };
      }

      const reachedMaxAnswers = state.currentAnswer.length === question.correct_answers_count;
      if (reachedMaxAnswers) return state;

      return {
        currentAnswer: [...state.currentAnswer, answer],
      };
    });
  };

  if (question.answers_type === 'ordering') {
    throw new Error('`TextAndImageAnswers` component does not support `ordering` mode.');
  }

  return (
    <Flex flexDir="column" gap="8" {...props}>
      <AnswerHeading>
        {isSingleChoice
          ? t('trivia.quiz.answers.single.heading')
          : t('trivia.quiz.answers.multiple.heading', {
              count: format(question.correct_answers_count),
              em: (chunks) => (
                <Text as="strong" fontSize="2xl" color="#408C57">
                  {chunks}
                </Text>
              ),
            })}
      </AnswerHeading>

      {question.answers_type === 'image' ? (
        <Grid alignSelf="center" templateColumns="1fr 1fr" gap="3">
          {question.answers.map((answer) => (
            <ImageAnswer
              key={answer.id}
              answer={answer}
              questionAnsweredCorrectly={questionAnsweredCorrectly}
              isDisabled={submitAnswerMutation.isLoading}
              onClick={() => onAnswerChange(answer)}
            />
          ))}
        </Grid>
      ) : (
        <Flex flexDir="column" gap={{base: 3, lg: 4}}>
          {question.answers.map((answer) => (
            <TextAnswer
              key={answer.id}
              answer={answer}
              questionAnsweredCorrectly={questionAnsweredCorrectly}
              isDisabled={submitAnswerMutation.isLoading}
              onClick={() => onAnswerChange(answer)}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};
